/**
 * The browser's side of Google sign-in.
 *
 * Google Identity Services arrives via a `<script>` tag rather than npm, so the
 * client keeps its zero runtime dependencies. That leaves a global object this
 * code does not control, which is exactly the shape a port exists for:
 * {@link GoogleSignIn} is the capability, {@link GsiGoogleSignIn} is the one
 * adapter that touches `window.google`, and every controller test uses a plain
 * fake instead.
 */

/** What Google hands back: `credential` is the ID token. */
export type GsiCredentialResponse = { readonly credential: string };

export type GoogleAccountsId = {
  initialize(config: {
    client_id: string;
    callback: (response: GsiCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }): void;
  renderButton(
    parent: HTMLElement,
    options: {
      type?: "standard" | "icon";
      theme?: "outline" | "filled_blue" | "filled_black";
      size?: "large" | "medium" | "small";
      text?: "signin_with" | "continue_with";
      shape?: "rectangular" | "pill";
      logo_alignment?: "left" | "center";
    },
  ): void;
  disableAutoSelect(): void;
};

/** The slice of `window` this module reads. */
export type GsiWindow = {
  readonly google?: { readonly accounts?: { readonly id?: GoogleAccountsId } };
};

export interface GoogleSignIn {
  /** Render the sign-in button, calling back with an ID token when used. */
  mount(
    container: HTMLElement,
    onCredential: (idToken: string) => void,
  ): Promise<void>;
  /** Forget the last account, so signing out actually signs out. */
  forgetUser(): void;
}

export const GSI_SCRIPT_ID = "gsi-client";
export const GSI_UNAVAILABLE = "Google sign-in could not be loaded.";

/**
 * Resolve once the Google library is usable.
 *
 * Event-driven rather than a polling loop: every outcome is reachable
 * synchronously in a test by dispatching an event, and there are no timers to
 * leak or fake.
 */
export function gsiReady(
  window: GsiWindow,
  script: HTMLElement | null,
): Promise<GoogleAccountsId> {
  const alreadyLoaded = window.google?.accounts?.id;

  if (alreadyLoaded !== undefined) return Promise.resolve(alreadyLoaded);

  if (script === null) return Promise.reject(new Error(GSI_UNAVAILABLE));

  return new Promise<GoogleAccountsId>((resolve, reject) => {
    script.addEventListener("load", () => {
      const loaded = window.google?.accounts?.id;

      if (loaded === undefined) {
        reject(new Error(GSI_UNAVAILABLE));
        return;
      }

      resolve(loaded);
    });

    script.addEventListener("error", () => reject(new Error(GSI_UNAVAILABLE)));
  });
}

export class GsiGoogleSignIn implements GoogleSignIn {
  private accounts: GoogleAccountsId | null = null;

  constructor(
    private readonly window: GsiWindow,
    private readonly document: Document,
    private readonly clientId: string,
  ) {}

  async mount(
    container: HTMLElement,
    onCredential: (idToken: string) => void,
  ): Promise<void> {
    const accounts = await gsiReady(
      this.window,
      this.document.getElementById(GSI_SCRIPT_ID),
    );

    this.accounts = accounts;

    // Signing out returns to the gate, which mounts again. Google appends its
    // button rather than replacing it, so without this a sign-out/sign-in cycle
    // leaves two of them stacked up.
    container.textContent = "";

    accounts.initialize({
      client_id: this.clientId,
      callback: (response) => {
        onCredential(response.credential);
      },
      // Popup is the default ux_mode. The redirect mode would have Google POST
      // cross-site to a login_uri, which SameSite=Lax would then strip the
      // session cookie from — so it is left alone deliberately.
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    accounts.renderButton(container, {
      type: "standard",
      theme: "outline",
      size: "large",
      text: "signin_with",
      shape: "pill",
      logo_alignment: "left",
    });
  }

  forgetUser(): void {
    // Without this, Google silently re-selects the same account on the next
    // load and "sign out" appears not to have worked.
    this.accounts?.disableAutoSelect();
  }
}
